/**
 * Phase 131 — StreamX Alpha Watch (Viewer)
 * Phase 131B — Realtime viewer count + chat polish (pin/emoji/slow-mode)
 * --------------------------------------------------------------------
 * PUBLIC page — no auth. Anyone with the link can watch.
 * Fetches GET /api/livex/streams/{id} which returns a viewer-only token.
 *
 * UI doctrine: investor-ready dark theme, high-contrast text, no opacity tricks.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LiveKitRoom, RoomAudioRenderer, GridLayout, ParticipantTile, useTracks,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import {
  Radio, ArrowLeft, AlertTriangle, Loader2, Heart, Trophy,
} from 'lucide-react';
import axios from 'axios';
import '@/styles/livex.css';
import LivexChatPanel from '@/components/livex/LivexChatPanel';
import LivexViewerBadge from '@/components/livex/LivexViewerBadge';
import LivexStreamEndDiscovery from '@/components/livex/LivexStreamEndDiscovery';
// Phase 140A — Broadcast Pro Lite: spectator scoreboard overlay + reactions bar
import ScoreboardOverlay from '@/components/livex/broadcast/ScoreboardOverlay';
import ReactionsBar from '@/components/livex/broadcast/ReactionsBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ViewerGrid() {
  // Subscribe to any camera tracks in the room — broadcaster's video lands here.
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: false }]);
  if (!tracks || tracks.length === 0) {
    return (
      <div className="absolute inset-0 grid place-items-center p-8 text-center">
        <div>
          <div className="h-16 w-16 rounded-2xl mx-auto mb-4 grid place-items-center border-2 border-[#22314F] bg-[#07111F]">
            <Radio className="h-7 w-7 text-[#FF5A1F] animate-pulse" />
          </div>
          <div className="text-lg font-black text-white mb-1">Waiting for broadcaster...</div>
          <p className="livex-body-text">The stream will start any moment now.</p>
        </div>
      </div>
    );
  }
  return (
    <GridLayout tracks={tracks} style={{ height: '100%' }}>
      <ParticipantTile disableSpeakingIndicator />
    </GridLayout>
  );
}

export default function LivexWatch() {
  const navigate = useNavigate();
  const { streamId } = useParams();
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewerName, setViewerName] = useState('');

  useEffect(() => {
    if (!streamId) { setError('Missing stream id'); setLoading(false); return; }
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/livex/streams/${streamId}`);
        if (!active) return;
        setStream(data);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.detail || 'Stream not found');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [streamId]);

  // Generate a friendly viewer name once (per-tab nickname).
  useEffect(() => {
    let n = sessionStorage.getItem('livex_viewer_name');
    if (!n) {
      const fan = `Fan${Math.floor(1000 + Math.random() * 9000)}`;
      sessionStorage.setItem('livex_viewer_name', fan);
      n = fan;
    }
    setViewerName(n);
  }, []);

  // Phase 140A — Poll the lightweight /live-state endpoint every 3s for the
  // scoreboard + reaction counters. Cheap (~50 byte payload), doesn't churn
  // the LiveKit viewer-token, and gives the overlay smooth updates without a
  // separate websocket fanout.
  const [liveState, setLiveState] = useState(null);
  useEffect(() => {
    if (!streamId) return undefined;
    let cancelled = false;
    async function pollOnce() {
      try {
        const { data } = await axios.get(`${API}/livex/streams/${streamId}/live-state`);
        if (!cancelled) setLiveState(data);
      } catch (_e) { /* best-effort */ }
    }
    pollOnce();
    const id = setInterval(pollOnce, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [streamId]);

  // Optimistic bump for reaction taps so the counter ticks before the next
  // poll completes. The real total comes back on the next 3s tick.
  const _localBumpReaction = (key, by = 1) => {
    setLiveState((prev) => prev ? ({
      ...prev,
      reactions: { ...(prev.reactions || {}), [key]: (prev.reactions?.[key] || 0) + by },
    }) : prev);
  };

  const mocked = stream?.mocked || !stream?.viewer_token;
  const ended = stream?.status === 'ended';

  const watchUrl = useMemo(() => (typeof window === 'undefined' ? '' : window.location.href), []);

  // ---------- Loading state ----------
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-white" style={{ background: '#0a0a0f' }} data-testid="livex-watch-loading">
        <div className="flex items-center gap-3 text-base font-bold">
          <Loader2 className="h-5 w-5 animate-spin text-[#FF5A1F]" /> Loading stream…
        </div>
      </div>
    );
  }

  // ---------- Error / not found ----------
  if (error || !stream) {
    return (
      <div className="min-h-screen grid place-items-center text-white px-6" style={{ background: '#0a0a0f' }} data-testid="livex-watch-error">
        <div className="max-w-md w-full text-center">
          <div className="h-16 w-16 rounded-2xl mx-auto mb-4 grid place-items-center bg-[#1a0e10] border-2 border-rose-700">
            <AlertTriangle className="h-7 w-7 text-[#FF5A1F]" />
          </div>
          <h1 className="text-2xl font-black text-white mb-2">Stream unavailable</h1>
          <p className="livex-body-text mb-6">{error || "We couldn't find this stream. It may have been removed."}</p>
          <button
            onClick={() => navigate('/streamx')}
            data-testid="livex-watch-error-back"
            className="inline-flex items-center gap-2 px-5 py-3 text-base font-bold text-white border-2 border-[#FF5A1F] rounded-lg"
            style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', fontSize: '16px' }}
          >
            <ArrowLeft className="h-4 w-4" /> Back to StreamX
          </button>
        </div>
      </div>
    );
  }

  // ---------- Layout shared between live + mocked ----------
  const TopBarLeft = (
    <div className="flex items-center gap-3 min-w-0">
      <button onClick={() => navigate('/streamx')} data-testid="livex-watch-back" className="flex-shrink-0 hover:opacity-80 transition-opacity">
        <ArrowLeft className="h-5 w-5 text-white" />
      </button>
      <div className="h-10 w-10 rounded-xl grid place-items-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
        <Radio className="h-5 w-5 text-white" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {!ended ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-rose-600 text-white text-[10px] font-black uppercase tracking-wider rounded">
              <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> LIVE
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-700 text-white text-[10px] font-black uppercase tracking-wider rounded">ENDED</span>
          )}
          {stream.sport && <span className="text-xs font-bold text-white capitalize">{stream.sport}</span>}
          {stream.academy_name && <span className="text-xs font-bold text-[#94A3B8]">· {stream.academy_name}</span>}
        </div>
        <h1 className="text-base font-bold text-white truncate max-w-[55vw] sm:max-w-md" data-testid="livex-watch-title">{stream.title}</h1>
      </div>
    </div>
  );

  const Description = stream.description ? (
    <div className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
      <div className="livex-label" style={{ marginBottom: '8px' }}>About this stream</div>
      <p className="livex-body-text">{stream.description}</p>
    </div>
  ) : null;

  const CallToActionStrip = (
    <div className="flex items-center gap-3 px-5 py-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
      <Heart className="h-5 w-5 text-[#FF5A1F] flex-shrink-0" />
      <span className="livex-body-text" style={{ fontSize: '14px' }}>Share the link, cheer in chat, follow the team.</span>
    </div>
  );

  // ---------- Mocked / ended path — no LiveKitRoom ----------
  if (mocked || ended) {
    return (
      <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-watch">
        <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
            {TopBarLeft}
            <span className="inline-flex items-center gap-2 text-sm font-bold text-white">{viewerName}</span>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="aspect-video bg-black rounded-xl overflow-hidden border-2 border-[#22314F] relative" data-testid="livex-watch-video">
              {ended ? (
                <div className="absolute inset-0 grid place-items-center p-6 text-center">
                  <div>
                    <Trophy className="h-12 w-12 mx-auto mb-3 text-amber-400" />
                    <div className="text-xl font-black text-white mb-1">🏁 Broadcast Ended</div>
                    <p className="livex-body-text">Thanks for tuning in. Catch another match below.</p>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 grid place-items-center p-6 text-center">
                  <div>
                    <div className="h-16 w-16 rounded-2xl mx-auto mb-4 grid place-items-center" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                      <Radio className="h-7 w-7 text-white animate-pulse" />
                    </div>
                    <div className="text-lg font-black text-white mb-2">Preview Mode</div>
                    <p className="livex-body-text max-w-md mx-auto">
                      LiveKit isn't configured yet. The stream metadata and chat UI are fully wired —
                      add LiveKit env vars in <span className="font-mono text-[#FF5A1F]">backend/.env</span> to enable live video.
                    </p>
                  </div>
                </div>
              )}
            </div>
            {Description}
            {CallToActionStrip}
            {ended && (
              <LivexStreamEndDiscovery
                currentStreamId={stream.id}
                sport={stream.sport}
              />
            )}
          </div>
          <div className="lg:row-start-1 lg:col-start-3 min-h-[400px]">
            {/* Read-only fallback — pinned banner still readable, but chat input is disabled visually */}
            <div className="flex flex-col h-full bg-[#07111F] border-2 border-[#22314F] rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b-2 border-[#22314F] bg-[#0c1a2e] flex items-center justify-between">
                <div className="livex-label" style={{ marginBottom: 0 }}>Live Chat</div>
                <span className="text-[10px] font-black uppercase tracking-[0.08em] px-2 py-0.5 bg-amber-500 text-black rounded">{ended ? 'Ended' : 'Preview'}</span>
              </div>
              {stream.pinned_message && (
                <div className="px-3 py-2.5 bg-[#1a0e10] border-b-2 border-[#3a1a1f] livex-body-text" data-testid="livex-chat-pinned">
                  <span className="font-bold text-[#FF5A1F] mr-2">PINNED</span>{stream.pinned_message}
                </div>
              )}
              <div className="flex-1 grid place-items-center p-6 text-center livex-body-text">
                {ended
                  ? 'Chat closed when the stream ended. Replay coming soon.'
                  : 'Chat opens automatically when the broadcaster goes live.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Real live path — wrap everything in LiveKitRoom ----------
  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-watch">
      <LiveKitRoom
        serverUrl={stream.server_url}
        token={stream.viewer_token}
        connect
        audio
        video={false}
        data-lk-theme="default"
      >
        <RoomAudioRenderer />
        {/* Top bar w/ live viewer badge */}
        <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
            {TopBarLeft}
            <div className="flex items-center gap-2 flex-shrink-0">
              <LivexViewerBadge />
              <span className="hidden sm:inline-flex text-sm font-bold text-white">{viewerName}</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="aspect-video bg-black rounded-xl overflow-hidden border-2 border-[#22314F] relative" data-testid="livex-watch-video">
              <ViewerGrid />
              {/* Phase 140A — Scoreboard overlay (top-left). Hidden if broadcaster has turned it off. */}
              <ScoreboardOverlay
                scoreboard={liveState?.scoreboard}
                className="absolute top-3 left-3 z-10"
              />
              {stream.plan_tier === 'pilot' && (
                <div
                  className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/75 backdrop-blur border-2 border-[#FF5A1F]/60 rounded-md text-[11px] font-black uppercase tracking-[0.12em] text-white inline-flex items-center gap-1.5 pointer-events-none"
                  data-testid="livex-watch-watermark"
                >
                  <Radio className="h-3 w-3 text-[#FF5A1F]" /> Powered by StreamX
                </div>
              )}
            </div>
            {/* Phase 140A — Reactions bar (live engagement) */}
            {!ended && (
              <div className="flex justify-center">
                <ReactionsBar
                  streamId={stream.id}
                  counts={liveState?.reactions}
                  onLocalBump={_localBumpReaction}
                />
              </div>
            )}
            {Description}
            {CallToActionStrip}
          </div>
          <div className="lg:row-start-1 lg:col-start-3 min-h-[400px]">
            <LivexChatPanel
              stream={stream}
              isBroadcaster={false}
              viewerName={viewerName || 'Viewer'}
            />
          </div>
        </div>
      </LiveKitRoom>
    </div>
  );
}
